<template>
  <v-data-table
    v-model:expanded="expanded"
    density="compact"
    no-data-text="No content packs available."
    :headers="<any>tableHeaders"
    :items="items"
    show-expand
    item-value="name"
    :loading="loading"
    :mobile="mobile"
    loading-text="Loading Content Pack Data..."
    items-per-page="-1">
    <!-- Download -->
    <template v-slot:item.website="{ item }">
      <v-tooltip content="Download">
        <template #activator="{ props }">
          <v-btn
            size="x-small"
            target="_blank"
            :href="(item as any).link"
            icon
            variant="text"
            color="accent"
            v-bind="props">
            <v-icon size="x-large">mdi-open-in-new</v-icon>
          </v-btn>
        </template>
      </v-tooltip>
    </template>
    <!-- Name -->
    <template v-slot:item.name="{ item }">
      {{ (item as any).name }}
    </template>
    <!-- Version -->
    <template v-slot:item.version="{ item }">
      {{ (item as any).version }}
      <span v-if="packInstalled(item)">
        <v-tooltip
          v-if="packOutdated(item)"
          max-width="300px"
          text="This content pack is installed but out of date, and may cause errors with the latest version of COMP/CON. Click this pack's Download button to get the latest version.">
          <template #activator="{ props }">
            <v-icon v-bind="props" color="accent">mdi-alert</v-icon>
          </template>
        </v-tooltip>
        <v-tooltip v-else max-width="300px" text="This content pack is installed and up-to-date">
          <template #activator="{ props }">
            <v-icon v-bind="props" color="success">mdi-check</v-icon>
          </template>
        </v-tooltip>
      </span>
    </template>
    <!-- v3 -->
    <template v-slot:item.v3="{ item }">
      <v-tooltip v-if="(item as any).v3">
        <template #activator="{ props }">
          <v-icon v-bind="props" color="success">mdi-check</v-icon>
        </template>
        This content pack is compatible with the latest version of COMP/CON and supports v3
        features.
      </v-tooltip>
      <v-tooltip v-else max-width="300px">
        <template #activator="{ props }">
          <v-icon v-bind="props" color="error">mdi-cancel</v-icon>
        </template>
        This content pack uses the v2 content format. It will function correctly but will lack
        features of v3-compatible packs. COMP/CON will not be able to manage effects or statuses
        from this pack in Active Mode.
      </v-tooltip>
    </template>
    <!-- cost -->
    <template v-slot:item.cost="{ item }">
      {{ (item as any).cost }}
    </template>
    <!-- Expanded view -->
    <template v-slot:expanded-row="{ columns, item }">
      <td :colspan="columns.length" class="pa-4 bg-panel">
        <v-row>
          <v-col>
            <p class="text-text pa-2 mb-1">
              <span v-if="(item as any).description" v-html-safe="(item as any).description" />
              <span v-else>No description given.</span>
            </p>

            <div v-if="(item as any).link" class="mt-2">
              <v-divider class="mt-1 mb-2" />
              <cc-button
                target="_blank"
                :href="(item as any).website"
                variant="plain"
                color="itch"
                class="mr-3">
                <v-icon prepend start>mdi-open-in-new</v-icon>
                itch.io page
              </cc-button>
              <cc-button
                target="_blank"
                :href="(item as any).website"
                variant="plain"
                color="secondary">
                <v-icon prepend start>mdi-open-in-new</v-icon>
                Author's Website
              </cc-button>
            </div>
          </v-col>
          <v-col v-if="(item as any).img" cols="2">
            <cc-img :src="(item as any).img" alt="Pack image" />
          </v-col>
        </v-row>
      </td>
    </template>
  </v-data-table>
</template>

<script lang="ts">
import { CompendiumStore } from '@/stores';
import semver from 'semver';
import { useMobile } from '@/mixins/useMobile';


export default {
  mixins: [useMobile],
  name: 'content-pack-directory-table',
  data: () => ({
    expanded: [],
  }),
  props: {
    items: {
      type: Array,
      required: true,
    },
    noAuthor: { type: Boolean },
    loading: { type: Boolean },
  },
  computed: {
    tableHeaders() {
      if (this.noAuthor)
        return [
          { title: '', key: !this.mobile && 'data-table-expand', width: '0' },
          {
            title: 'Download',
            width: '0',
            key: 'website',
            sortable: false,
            align: 'center',
          },
          { title: 'Name', key: 'name' },
          { title: 'Version', key: 'version' },
          { title: 'v3', value: 'v3' },
          { title: 'Cost', key: 'cost' },
        ];
      return [
        { title: '', key: !this.mobile && 'data-table-expand', width: '0' },
        { title: 'Name', key: 'title' },
        { title: 'Author', key: 'author' },
        { title: 'Version', key: 'version' },
        { title: 'v3', value: 'v3' },
        { title: 'Cost', key: 'cost' },
        {
          key: 'website',
          sortable: false,
          align: 'center',
          width: '0',
        },
      ];
    },
    contentPacks() {
      return CompendiumStore().ContentPacks;
    },
  },
  methods: {
    packInstalled(item) {
      return this.contentPacks.some(({ Name, Manifest }) =>
        [Name, Manifest.name].some((n) =>
          [item.name, item.title].some((i) => n?.toLowerCase() === i?.toLowerCase())
        )
      );
    },
    packOutdated(item) {
      const installedPack = this.contentPacks.find(
        (x) => x.Name === item.name || x.Name === item.title
      );
      if (!installedPack) return true;
      return !semver.gte(semver.coerce(installedPack.Version), semver.coerce(item.version));
    },
  },
};
</script>
